<template>
    <div class="main" :class="communication === PLAYER_COMMUNICATION.INLINE ? 'is-inline' : null">
        <div class="background">
            <div class="background-color"
                 :style="background.color"
            />
            <div v-for="backgroundImage of background.images"
                 class="background-image"
                 :style="{
                    backgroundImage: 'url(' + backgroundImage.src + ')',
                    opacity: backgroundImage.visible ? 1 : 0,
                    transition: backgroundImage.transition,
                   filter: backgroundImage.blur ? `blur(${ceremony?.company?.image_blur_amount ? ceremony.company.image_blur_amount : 20}px)` : 'none',
                 }"
            />
        </div>

        <div class="content-wrapper">
            <transition-group name="fade">
                <photo-slider
                    :playing="playing && renderState === 'images'"
                    :images="imagesToShow"
                    :current-image-index="currentImageIndex"
                    v-show="renderState === 'images'"
                    :fade-time="imageFadeTime"
                    :key="1"
                    :block-id="fadeId"
                    :style="{ transition: imageFadeTime + 'ms linear opacity' }"
                />

                <playlist-video-player
                    :max-volume="videoMaxVolume"
                    :max-duration="maxDuration"
                    :playing="playing && renderState === 'videos'"
                    :playlist="videosToShow"
                    :autoplay="renderState === 'videos'"
                    :key="2"
                    :controls="false"
                    v-show="renderState === 'videos'"
                    :fade-time="videoFadeTime"
                    :can-fade="true"
                    :set-duration="setCurrentDuration"
                    :style="{ transition: videoFadeTime + 'ms linear opacity' }"
                    :loop="block && block.ceremony.type === 'ledwall' && !videoFadeTime"
                    ref="videoPlayer"
                />
                <live-video-player
                    v-if="communication !== PLAYER_COMMUNICATION.INLINE && streamingConfig && livestream"
                    :max-volume="1"
                    :livestream="livestream"
                    :playing="playing && renderState === 'live-video'"
                    :autoplay="renderState === 'live-video'"
                    :key="3"
                    :controls="false"
                    v-show="renderState === 'live-video'"
                    :fade-time="videoFadeTime"
                    :can-fade="true"
                    :set-duration="setCurrentDuration"
                    :style="{ transition: videoFadeTime + 'ms linear opacity' }"
                    ref="liveVideoPlayer"
                    :token="'ceremony:' + livestream"
                />
            </transition-group>
        </div>

        <song-player
            :playing="audioPlaying && (renderState === 'images' || renderState === 'audio-only' || renderState === 'videos')"
            :autoplay="renderState === 'images' || renderState === 'audio-only' || renderState === 'videos'"
            :playlist="songs"
            :max-duration="maxDuration"
            :set-duration="setCurrentDuration"
            v-if="songs.length"
            class="song-player"
            :fade-time="songFadeTime"
            :can-fade="true"
            :key="'player_' + songPlayerIndex"
            ref="audioPlayer"
        />

        <div v-if="showActivePlayerIndicator" class="active-player-indicator">{{ showActivePlayerIndicator }}</div>
    </div>
</template>

<script>

    import PlaylistVideoPlayer from '../../components/PlaylistVideoPlayer';
    import PhotoSlider from '../../components/PhotoSlider';
    import SongPlayer from '../../components/SongPlayer';
    import FileMixin from '../../mixins/FileMixin';
    import getImageForDuration from '../../utils/getImageForDuration';
    import {EventBus} from '../../utils/eventBus';
    import {getMaxDurationFromBlock} from '../../utils/getMaxDurationForBlock';
    import getVideoForDuration from '../../utils/getVideoForDuration';
    import LiveVideoPlayer from '../../components/LiveVideoPlayer.vue';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';
    import { mapState } from 'vuex';

    export default {

        props: {
            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },
        },

        components: {
            SongPlayer,
            PhotoSlider,
            LiveVideoPlayer,
            PlaylistVideoPlayer,
        },

        mixins: [
            FileMixin,
        ],

        created() {
            switch (this.communication) {
                case PLAYER_COMMUNICATION.WEBSOCKET:
                    EventBus.$on('WebsocketReconnect', this.setupWebsocketEvents);

                    this.setupWebsocketEvents();
                    break;
                case PLAYER_COMMUNICATION.INLINE:
                    this.setupInlineEvents();
                    break;
                case PLAYER_COMMUNICATION.IPC:
                    this.setupIpcEvents();
                    break;
            }

            this.intervalId = setInterval(() => {
                if (
                    !this.block ||
                    !this.block.id ||
                    !this.canUpdateDuration ||
                    (this.renderState === 'none' && this.block.files.length >= 1) ||
                    this.currentDuration > this.maxDuration ||
                    (this.block && this.block && this.block.ceremony.type === 'ledwall')
                ) {
                    if (!this.shouldFinishing) {
                        return;
                    }
                }

                this.currentDuration += 0.1;

                const currentBlockType = this.block?.type || null;

                if (Math.floor(this.currentDuration) !== this.currentDurationInSeconds) {
                    this.currentDurationInSeconds++;
                    this.updateBackground(this.block);

                    this.updateDuration(this.currentDurationInSeconds);

                    // Send signal we are finished
                    if (this.currentDuration >= this.maxDuration && currentBlockType !== 7/* call */) {
                        this.finishBlock();
                        return;
                    }
                }

                // For video's we want to use the default transition time since the video will be force stopped meaning the block will be set to null when stopped
                // This is should be properly fixed in player v2
                const transitionTime = this.renderState === 'videos' ? 1.9 : (this.transitionDuration / 1000);

                // Start with fading on time
                if (this.renderState !== 'none' && !this.shouldFinishing && this.currentDuration >= this.maxDuration - transitionTime) {
                    if (this.communication !== PLAYER_COMMUNICATION.INLINE && currentBlockType !== 7/* call */) {
                        this.shouldFinishing = true;
                        this.stop(this.renderState === 'videos');
                    }
                }

                // Send signal we are finished
                if (this.currentDuration >= this.maxDuration && currentBlockType !== 7/* call */) {
                    this.finishBlock();
                }
            }, 100);
        },

        // watch: {
        //     background(newBackground, oldBackground) {
        //         const newBackgroundString = JSON.stringify(newBackground);
        //
        //         if (newBackgroundString !== JSON.stringify(oldBackground)) {
        //             console.log('Background changed to: ' + newBackgroundString);
        //         }
        //     }
        // },

        computed: {
            PLAYER_COMMUNICATION() {
                return PLAYER_COMMUNICATION;
            },

            streamingConfig() {
                return this.companySettings?.streaming || window.PLAYER_STREAMING;
            },

            imagesToShow() {
                return this.images.filter((image) => image.blockId === (this.block?.id)).map((image) => {
                    return image.src;
                });
            },

            videosToShow() {
                return this.videos;
            },

            currentImageIndex() {
                if (!this.imagesToShow) {
                    return null;
                }

                if (this.activeFileIndex !== null) {
                    return this.activeFileIndex;
                }

                if (this.block && this.block.has_manual_action) {
                    return this.activeFileIndex;
                }

                const imageForDuration = getImageForDuration(this.ceremony, this.block, this.imagesToShow, this.currentDuration, this.block ? this.block.isRepeating : false);

                if (imageForDuration === null) {
                    return this.imagesToShow.length - 1;
                }

                return imageForDuration;
            },

            maxDuration() {
                if (!this.block) {
                    return 0;
                }

                return getMaxDurationFromBlock(this.ceremony, this.block, this.block.isRepeating);
            },

            transitionDuration() {
                if (this.videos.length) {
                    return this.videoFadeTime;
                }

                if (this.images.length) {
                    return this.imageFadeTime;
                }

                return this.defaultFadeTime;
            },

            showActivePlayerIndicator() {
                console.log('Indicator: ', !!this.block, this.isFading, this.isStarting);
                if (this.block || this.isFading || this.isStarting) {
                    return false;
                }

                if (this.renderState === 'none' || this.renderState === null) {
                    return window.showActivePlayerIndicator;
                }

                return false;
            },

            ...mapState({
                companySettings: state => state.companySettings,
            }),
        },

        data() {
            return {
                block: null,
                ceremony: null,

                songs: [],
                images: [],
                videos: [],
                livestream: null,

                videoMaxVolume: 1,

                currentDuration: 0,
                currentDurationInSeconds: 0,
                setCurrentDuration: null,
                setCurrentDurationTimeout: null,
                canUpdateDuration: true,

                playing: false,
                audioPlaying: false,

                defaultFadeTime: 1900,

                songFadeTime: 1900,
                imageFadeTime: 1900,
                videoFadeTime: 1900,

                resetFadeTimeTimeout: null,
                deleteBlockTimeout: null,

                fadeId: null,
                state: null,
                renderState: null,

                songTimeout: null,

                isFading: false,
                isStarting: false,

                activeFileIndex: null,
                intervalId: null,

                lastFileChange: null,

                songPlayerIndex: 0,

                background: {
                    color: {
                        backgroundColor: null,
                        transition: null,
                    },
                    images: [],
                },

                shouldFinishing: false,
            };
        },

        mounted() {
            this.updateBackground(null);
            this.setInitialVolume();
        },

        methods: {
            setRenderState(state) {
                console.log('Set render state to ' + state);
                this.renderState = state;
            },

            setupInlineEvents() {
                EventBus.$on('BlockStarted', this.blockStartedEvent);
                EventBus.$on('BlockStateChanged', this.blockStateChangedEvent);
                EventBus.$on('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                EventBus.$on('CeremonyFinished', this.ceremonyFinishedEvent);
            },

            setupIpcEvents() {
                window.addEventListener('ipc-from-controller', ({ detail }) => {
                    switch (detail.action) {
                        case 'BlockStarted':
                            return this.blockStartedEvent(detail.data);
                        case 'BlockStateChanged':
                            return this.blockStateChangedEvent(detail.data);
                        case 'BlockActiveFileChanged':
                            return this.blockActiveFileChangedEvent(detail.data);
                        case 'CeremonyFinished':
                            return this.ceremonyFinishedEvent(detail.data);
                    }
                });
            },

            setupWebsocketEvents() {
                window.stopListening();

                const channel = Echo.channel('blockmessages');

                channel.listen('PreloadFiles', (event) => {
                    for (const file of event.files) {
                        const existing = this.$root.preloadQueue.getItem(file);

                        if (existing) {
                            continue;
                        }

                        this.$root.preloadQueue.loadFile(file);

                        console.log('Adding file for preload: ' + file);
                    }
                });

                channel.listen('BlockStarted', this.blockStartedEvent);
                channel.listen('BlockStateChanged', this.blockStateChangedEvent);
                channel.listen('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                channel.listen('CeremonyFinished', this.ceremonyFinishedEvent);
                channel.listen('VolumeUpdated', this.volumeUpdated);
            },

            async blockStartedEvent(event) {
                this.defaultFadeTime = event.default_transition_time === undefined ? 1900 : event.default_transition_time;

                if (this.deleteBlockTimeout) {
                    clearTimeout(this.deleteBlockTimeout);
                }

                if (this.resetFadeTimeTimeout) {
                    if (this.imageFadeTime !== this.defaultFadeTime) {
                        this.imageFadeTime = this.defaultFadeTime;
                    }

                    if (this.videoFadeTime !== this.defaultFadeTime) {
                        this.videoFadeTime = this.defaultFadeTime;
                    }

                    clearTimeout(this.resetFadeTimeTimeout);
                }

                this.isStarting = true;

                // Wait for the fade time to reset
                await new Promise(resolve => {
                    setTimeout(() => {
                        resolve();
                    }, 100);
                });

                const block = event.block;
                const isFixedBlock = block ? block.settings.find(setting => setting.name === 'fixed_block' && setting.value === '1') : false;


                // When there is no change in the file id we do not need to show a fade
                const currentImageSrc = this.images[this.currentImageIndex]?.src;
                const newImageSrc = event.file ? event.file?.public_path : {
                    ...(block?.subblocks?.length ? block.subblocks[0] : block),
                }?.files?.[0]?.public_path;

                const fadeTime = currentImageSrc && newImageSrc && currentImageSrc === newImageSrc.replace(':code', block?.ceremony?.code) ? 0 : event.initial_transition_time;

                if (isFixedBlock) {
                    // We want to fade in but not fades between videos / images
                    this.resetFadeTimeTimeout = setTimeout(function () {
                        this.imageFadeTime = 0;
                        this.videoFadeTime = 0;
                    }.bind(this), (Number(this.defaultFadeTime) * 2) + 300);
                } else if (fadeTime !== null && fadeTime !== undefined) {
                    this.imageFadeTime = Number(fadeTime);
                    this.videoFadeTime = Number(fadeTime);

                    this.resetFadeTimeTimeout = setTimeout(function () {
                        this.imageFadeTime = this.defaultFadeTime;
                        this.videoFadeTime = this.defaultFadeTime;
                    }.bind(this), Number(fadeTime) + 300);
                } else {
                    this.imageFadeTime = this.defaultFadeTime;
                    this.videoFadeTime = this.defaultFadeTime;
                }

                const file = event.file;

                block.isRepeating = event.should_repeat;

                console.log('Block started', block, block.id);

                if (file) {
                    console.log('Initial file', file, file.id);
                }

                this.ceremony = block.ceremony;

                this.isStarting = false;

                if (block.parent_block) {
                    return this.startBlock(block, file);
                }

                return this.startBlock({
                    ...(block.subblocks.length ? block.subblocks[0] : block),
                    isRepeating: block.isRepeating,
                    ceremony: block.ceremony,
                }, file);
            },

            blockStateChangedEvent(event) {
                console.log('Changed state', event.state);

                if (event.state === 'duration') {
                    this.setDuration(event.duration || 0);
                    this.updateBackground(this.block);

                    if (this.state === 'playing' || this.state === null || this.state === 'none') {
                        this.state = 'playing';
                        this.play();
                    }

                    return;
                }

                this.state = event.state;

                switch (this.state) {
                    case 'stopped':
                        this.stop(true, event.default_transition_time);
                        break;
                    case 'paused':
                        this.pause();
                        break;
                    case 'playing':
                        this.play();
                        break;
                }
            },

            setDuration(duration) {
                this.currentDuration = duration || 0;
                this.currentDurationInSeconds = this.currentDuration;
                this.setCurrentDuration = this.currentDuration;

                // Reset variable to null
                if (this.setCurrentDurationTimeout) {
                    clearTimeout(this.setCurrentDurationTimeout);
                }

                this.setCurrentDurationTimeout = setTimeout(function () {
                    this.setCurrentDuration = null;
                }.bind(this), 100);
            },

            async blockActiveFileChangedEvent(event) {
                console.log('Changed active file', event.file.id);

                if (!this.block || event.block.id !== this.block.id) {
                    this.blockStartedEvent(event);
                } else {
                    if (!this.playing) {
                        this.play();
                    }

                    this.activeFileIndex = this.getFileIndex(event.file, event.block);
                }
            },

            getFileIndex(file, block) {
                return this.images.filter((image) => image.blockId === (block?.id)).findIndex(image => image.src === file.public_path.replace('/:code/', '/' + block.ceremony.code + '/'));
            },

            ceremonyFinishedEvent() {
                console.log('Ceremony finished');
                this.stop();
            },

            volumeUpdated(event) {
                console.log('Set volume to ' + event.volume);

                if (window.setVolume) {
                    window.setVolume(event.volume);
                }
            },

            startBlock(block, activeFile = undefined) {
                this.shouldFinishing = false;

                if (!block) {
                    this.stop();

                    return;
                }

                this.isFading = true;
                this.canUpdateDuration = false;
                this.currentDuration = 0;
                this.currentDurationInSeconds = 0;
                this.videoMaxVolume = 1;

                if (!this.fadeId) {
                    this.fadeId = block.id;
                }

                const keepPlaying = Boolean(
                    this.block &&
                    this.block.parent_block &&
                    block.parent_block &&
                    this.block.parent_block.id === block.parent_block.id &&
                    this.block.id !== block.id,
                );

                const promises = [];
                const songs = (block.parent_block ? block.parent_block : block).files.filter(file => {
                    return !!file.song;
                });

                promises.push(this.setSongs(block, songs, keepPlaying));

                this.setRenderState(keepPlaying ? 'audio-only' : 'none');

                this.updateBackground(null);

                const videos = this.getVideosFromBlock(block);
                const images = this.getImagesFromBlock(block);

                if (videos.length) {
                    if (songs.length) {
                        this.videoMaxVolume = 0;
                    }

                    promises.push(this.setVideos(block, videos));
                } else {
                    this.setVideos(null, []);
                }

                if (images.length) {
                    promises.push(this.setImages(block, images, activeFile));
                } else if (!videos.length && songs.length) {
                    this.setRenderState('audio-only');
                    this.setImages(null, []);
                }

                if (block.type === 7/*live*/ && this.streamingConfig) {
                    this.setRenderState('live-video');
                    this.setImages(null, []);
                    this.setVideos(null, []);
                    this.livestream = block.livestream;
                } else {
                    this.livestream = null;
                }

                Promise.all(promises).then(() => {
                    this.updateBackground(block);
                    this.lastFileChange = this.getFileChangedIndex();

                    this.isFading = false;
                    this.canUpdateDuration = true;

                    if (!this.playing || !this.audioPlaying) {
                        this.play();
                    }
                });

                this.block = block;

                if (this.songs.length <= 0 && this.videosToShow.length <= 0) {
                    this.play();

                    this.isFading = false;
                }
            },

            play() {
                console.log('Playing player');
                this.playing = true;
                this.audioPlaying = true;
                this.canUpdateDuration = true;
            },

            pause() {
                console.log('Pausing player');
                this.playing = false;
                this.audioPlaying = false;
                this.canUpdateDuration = false;
            },

            async stop(force = false, fadeOutTime = 1900) {
                console.log('Stopping player');
                this.canUpdateDuration = false;
                this.livestream = null;

                this.setFadeOutTiming(fadeOutTime);

                // we want to hide the last picture when we stop on purpose
                if (force) {
                    this.updateBackground(null);

                    this.setRenderState('none');

                    // Set the block null
                    this.deleteBlockTimeout = setTimeout(function () {
                        this.block = null; // We only want to set the block to null on force
                    }.bind(this), this.transitionDuration);
                }
            },

            setFadeOutTiming(fadeOutTime = 1900) {
                console.log('Set fadeout timing to ' + fadeOutTime);

                if (this.imageFadeTime === fadeOutTime || !this.playing) {
                    return;
                }

                this.imageFadeTime = fadeOutTime;
                this.videoFadeTime = fadeOutTime;
                this.songFadeTime = fadeOutTime;

                setTimeout(function () {
                    this.imageFadeTime = this.defaultFadeTime;
                    this.videoFadeTime = this.defaultFadeTime;
                    this.songFadeTime = this.defaultFadeTime;
                }.bind(this), fadeOutTime);
            },

            setSongs(block, songs, keepPlaying = false) {
                const buildedSongs = this.buildSongs(block, songs);

                return new Promise((resolve) => {
                    if (this.songs.length) {
                        if (keepPlaying) {
                            resolve();

                            return;
                        }

                        this.audioPlaying = false;

                        if (this.songTimeout) {
                            clearTimeout(this.songTimeout);
                        }

                        if (buildedSongs.length <= 0 && this.songs.length <= 0) {
                            this.songs = buildedSongs;

                            resolve();

                            return;
                        }

                        this.songTimeout = setTimeout(() => {
                            this.songs = buildedSongs;

                            this.songPlayerIndex++;

                            resolve();
                        }, this.songFadeTime);

                        return;
                    }

                    this.songs = buildedSongs;

                    resolve();
                });
            },

            buildSongs(block, songs) {
                const parentBlock = (block && block.parent_block ? block.parent_block : block);
                const repeatAudio = block ? parentBlock.settings.find(setting => setting.name === 'repeat_audio' && setting.value === '1') : false;
                const isFixedBlock = block ? parentBlock.settings.find(setting => setting.name === 'fixed_block' && setting.value === '1') : false;

                const shouldRepeat = repeatAudio || isFixedBlock || (block && block.isRepeating);

                if (block) {
                    const isNormalBlock = parentBlock.type === 1 /*1 = image */ ||
                        parentBlock.type === 3 /*3 = spreker */ ||
                        (parentBlock.type === 4 && block.type === 1); // check if parent is media and subblock is image

                    if (shouldRepeat && isNormalBlock && !block.isRepeating) {
                        return songs.map(file => {
                            return {
                                duration: Number(file.song.duration),
                                src: file.public_path,
                            };
                        });
                    }
                }

                if (!shouldRepeat && !(block && block.songs_override_id && block.songs_override_id !== block.id)) {
                    return songs.map(file => {
                        return {
                            duration: Number(file.song.duration),
                            src: file.public_path,
                        };
                    });
                }

                if (songs.length <= 0) {
                    return songs;
                }

                const songsDuration = songs.reduce((amount, file) => amount + file.song.duration, 0);
                const maxDuration = getMaxDurationFromBlock(this.ceremony, block, block.isRepeating);

                const ceremonyDuration = this.ceremony.duration <= maxDuration ? maxDuration : this.ceremony.duration;
                const missingDuration = ceremonyDuration - songsDuration;

                if (missingDuration <= 0) {
                    return songs.map(file => {
                        return {
                            duration: Number(file.song.duration),
                            src: file.public_path,
                        };
                    });
                }

                const songsCountToAppend = Math.ceil(missingDuration / songsDuration);
                const allSongs = [
                    ...songs,
                ];

                for (let appendCount = 0; appendCount <= songsCountToAppend; appendCount++) {
                    for (const song of songs) {
                        allSongs.push(song);
                    }
                }

                return allSongs.map(file => {
                    return {
                        duration: Number(file.song.duration),
                        src: file.public_path,
                    };
                });
            },

            setVideos(block, videos) {
                return new Promise((resolve) => {
                    if (this.videosToShow.length || this.imagesToShow.length) {
                        this.playing = false;

                        if (this.videoTimeout) {
                            clearTimeout(this.videoTimeout);
                        }

                        if (videos.length <= 0 && this.videosToShow.length <= 0) {
                            this.videos = videos;

                            resolve();

                            return;
                        }

                        const handleVideo = () => {
                            this.videos = videos;

                            if (this.videos.length) {
                                this.setRenderState('videos');
                            }

                            resolve();
                        };

                        if (this.videoFadeTime <= 0) {
                            handleVideo();
                        } else {
                            this.videoTimeout = setTimeout(handleVideo, this.videoFadeTime);
                        }

                        return;
                    }

                    this.videos = videos;

                    if (this.videos.length) {
                        this.setRenderState('videos');
                    }

                    resolve();
                });
            },

            setImages(block, images, activeFile) {
                return new Promise((resolve) => {
                    if (this.videos.length || this.imagesToShow.length) {
                        this.playing = false;

                        if (this.imageTimeout) {
                            clearTimeout(this.imageTimeout);
                        }

                        if (images.length <= 0 && this.imagesToShow.length <= 0) {
                            this.images = images;

                            this.activeFileIndex = null;

                            resolve();

                            return;
                        }

                        const handleImage = () => {
                            this.images = images;
                            this.fadeId = block ? block.id : null;

                            if (activeFile) {
                                this.activeFileIndex = this.getFileIndex(activeFile, block);
                            } else {
                                this.activeFileIndex = null;
                            }

                            if (this.images.length) {
                                this.setRenderState('images');
                            }

                            resolve();
                        };

                        if (this.imageFadeTime <= 0) {
                            handleImage();
                        } else {
                            this.imageTimeout = setTimeout(handleImage, this.imageFadeTime);
                        }

                        return;
                    }

                    this.images = images;

                    if (activeFile) {
                        this.activeFileIndex = this.getFileIndex(activeFile, block);
                    } else {
                        this.activeFileIndex = null;
                    }

                    if (this.images.length) {
                        this.setRenderState('images');
                    }

                    resolve();
                });
            },

            getCurrentVolume() {
                if (this.$refs.videoPlayer) {
                    return this.$refs.videoPlayer.getCurrentVolume();
                }
                if (this.$refs.audioPlayer) {
                    return this.$refs.audioPlayer.getCurrentVolume();
                }
                if (this.$refs.liveVideoPlayer) {
                    return this.$refs.liveVideoPlayer.getCurrentVolume();
                }
                return 'no-audio';
            },

            async updateDuration(duration) {
                if (!this.block || !this.block.id || !this.canUpdateDuration) {
                    if (!this.shouldFinishing) {
                        return;
                    }
                }

                console.log('Duration', duration, this.maxDuration, this.renderState, this.getCurrentVolume());

                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            const fileChangedIndex = this.getFileChangedIndex();
                            let changedFile = false;

                            if (this.lastFileChange !== fileChangedIndex) {
                                this.lastFileChange = fileChangedIndex;
                                changedFile = true;
                            }

                            await this.$http.post('/api/player/blocks/duration-update', {
                                duration,
                                max_duration: this.maxDuration,
                                id: this.block?.id,
                                changed_file: changedFile,
                            });
                        } catch (error) {
                            console.log(error);
                        }

                        break;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        this.emitAction('BlockDurationChanged', {
                            duration: parseInt(duration),
                            max_duration: parseInt(this.maxDuration),
                        });
                        break;
                }
            },

            getFileChangedIndex() {
                if (!this.block) {
                    return null;
                }

                if (this.block.has_manual_action) {
                    return this.activeFileIndex ? this.activeFileIndex : 0;
                }

                const block = this.block.parent_id ? this.block : this.block.subblocks[0];

                if ((!this.block.parent_id && this.block.block_type === 1) || this.block.type === 1) {
                    return getImageForDuration(this.ceremony, block, block.files, this.currentDuration, this.block ? this.block.isRepeating : false);
                }

                if ((!this.block.parent_id && this.block.block_type === 2) || this.block.type === 2) {
                    return getVideoForDuration(block, block.files, this.currentDuration);
                }

                return null;
            },

            getImagesFromBlock(block) {
                return block.files.filter(file => this.isImage(file)).map((file) => {
                    return {
                        blockId: block.id,
                        src: file.public_path.replace('/:code/', '/' + this.ceremony.code + '/'),
                    };
                });
            },

            getVideosFromBlock(block) {
                const videos = block.files.filter(file => this.isVideo(file)).map((file, fileIndex) => {
                    return {
                        duration: Number(file.duration),
                        blockId: block.id,
                        src: file.public_path.replace('/:code/', '/' + this.ceremony.code + '/'),
                    };
                });

                if (block.ceremony.type === 'ledwall' && !this.videoFadeTime) {
                    return videos.length ? [videos[0]] : [];
                }

                const shouldRepeat = block.settings.find(setting => setting.name === 'repeat_videos' && setting.value === '1');
                const isFixed = (block.parent_block ? block.parent_block : block).settings.find(setting => setting.name === 'fixed_block' && setting.value === '1');

                if ((shouldRepeat || isFixed || block.isRepeating) && videos.length) {
                    const totalDuration = videos.reduce((amount, video) => amount + video.duration, 0);
                    const maxDuration = getMaxDurationFromBlock(this.ceremony, block, block.isRepeating);

                    if (totalDuration < maxDuration) {
                        const toAdd = Math.ceil((maxDuration - totalDuration) / totalDuration);

                        return [].concat.apply([], Array(toAdd + 1).fill(videos));
                    }
                }

                return videos;
            },

            async finishBlock(nextBlock) {
                this.shouldFinishing = false;

                if (nextBlock && (!this.block || this.block.id === nextBlock.id)) {
                    return;
                }

                if (!this.block) {
                    return;
                }

                this.stop(this.renderState === 'videos');

                console.log('Mark block as finished', this.block, this.block?.id);

                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            await this.$http.post('/api/player/blocks/finished', {
                                id: this.block.id,
                            });
                        } catch (error) {
                            console.log(error);
                        }

                        break;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        this.emitAction('BlockFinished', {
                            nextBlock: nextBlock ? {
                                ...nextBlock,
                                ceremony: this.ceremony,
                            } : undefined,
                            block: {
                                ...this.block,
                                ceremony: this.ceremony,
                            },
                        });

                        break;
                }
            },

            getNextBlock(block) {
                if (!this.ceremony || !this.ceremony.blocks || !this.ceremony.blocks.length || !this.ceremony.blocks[0].subblocks.length) {
                    return null;
                }

                if (!block) {
                    return this.ceremony.blocks[0].subblocks[0];
                }

                const parentBlockIndex = this.ceremony.blocks.findIndex(blockToFind => blockToFind.subblocks.findIndex(subBlockToFind => subBlockToFind.id === block.id) !== -1);

                if (parentBlockIndex === -1) {
                    return this.ceremony.blocks[0].subblocks[0];
                }

                const parentBlock = this.ceremony.blocks[parentBlockIndex];
                const subBlockIndex = parentBlock.subblocks.indexOf(block);

                if (subBlockIndex === -1 || subBlockIndex + 1 > parentBlock.subblocks.length - 1) {
                    if (parentBlockIndex + 1 > this.ceremony.blocks.length - 1) {
                        return null;
                    }

                    return this.ceremony.blocks[parentBlockIndex + 1].subblocks[0];
                }

                return parentBlock.subblocks[subBlockIndex + 1];
            },

            updateBackground(block) {
                if (!block) {
                    console.log('Reset background, no block');
                    this.background = this.getDefaultBackground('#000000');

                    return;
                }

                const settings = block?.settings || [];
                const backgroundType = settings.find(setting => setting.name === 'background_type');

                switch (backgroundType?.value) {
                    case 'color':
                        // Color is set via the block
                        const backgroundColor = settings.find(setting => setting.name === 'background_color');
                        this.background = this.getDefaultBackground(backgroundColor?.value || '#000000');

                        break;
                    case 'photo_blur':
                        const backgroundWithBlurredImages = this.getDefaultBackground('#000000', false);

                        backgroundWithBlurredImages.images = this.imagesToShow.map((image, index) => {
                            return {
                                src: image,
                                visible: this.currentImageIndex === index,
                                transition: `${this.imageFadeTime || 0}ms ease opacity`,
                                blur: true,
                            };
                        });

                        this.background = backgroundWithBlurredImages;

                        break;
                    case 'photo':
                        const backgroundWithImage = this.getDefaultBackground('#000000', false);

                        const backgroundPhotoSetting = block.settings.find(find_setting => find_setting.name === 'background_photo');

                        if (backgroundPhotoSetting) {
                            const backgroundPhoto = JSON.parse(backgroundPhotoSetting.value);
                            const url = backgroundPhoto.public_path.replace(/\/:code\//g, '/' + this.ceremony.code + '/');

                            backgroundWithImage.images = [
                                {
                                    src: url,
                                    visible: true,
                                    transition: `${this.imageFadeTime || 0}ms ease opacity`,
                                    blur: true,
                                },
                            ];
                        }

                        this.background = backgroundWithImage;

                        break;
                    default:
                        // When we have no settings just reset the color
                        this.background = this.getDefaultBackground('#000000');
                        break;
                }
            },

            getDefaultBackground(color, resetImages = true) {
                // clone the default background
                const background = {
                    ...this.background,
                };

                background.color = {
                    backgroundColor: color,
                    transition: `${this.imageFadeTime || 0}ms ease background`,
                };

                // Only hide the images, do not remove them because then we are missing the transition
                background.images = background.images.map(image => {
                    image.visible = false;

                    return image;
                });

                // Add empty placeholder for transition...
                if (resetImages && !background.images.length) {
                    background.images.push({
                        src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkqAcAAIUAgUW0RjgAAAAASUVORK5CYII=',
                        visible: false,
                        transition: `${this.imageFadeTime || 0}ms ease opacity`,
                    });
                }

                return background;
            },

            async setInitialVolume() {
                if (!window.setVolume || this.communication !== PLAYER_COMMUNICATION.WEBSOCKET) {
                    return;
                }

                try {
                    const response = await this.$http.get('/api/player/volume');
                    window.setVolume(response.data.volume);
                } catch (exception) {
                    console.error(exception);
                }
            },

            emitAction(action, data) {
                if (this.communication === PLAYER_COMMUNICATION.INLINE) {
                    EventBus.$emit(action, data);
                } else if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    window.dispatchEvent(new CustomEvent('ipc-to-controller', {
                        detail: {
                            action,
                            data,
                        },
                    }));
                }
            },
        },

        beforeDestroy() {
            if (this.communication === PLAYER_COMMUNICATION.WEBSOCKET) {
                const channel = Echo.channel('blockmessages');

                channel.stopListening('BlockStarted');
                channel.stopListening('BlockStateChanged');
                channel.stopListening('CeremonyFinished');
            }

            if (this.intervalId) {
                clearInterval(this.intervalId);
            }
        },
    };

</script>
<style lang="scss" scoped>

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .main {
        height: 100vh;
        width: 100vw;
        cursor: none;
        pointer-events: none;
        z-index: 1;

        &.is-inline {
            width: 100%;
            height: 100%;
        }

        .background {
            &,
            & > * {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: -1;
            }

            .background-image {
                background-color: #000000;
                background-position: center;
                background-repeat: no-repeat;
                background-size: cover;
            }
        }
    }

    .content-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;

        > span {
            width: 100%;
        }
    }

    .song-player {
        opacity: 0;
        pointer-events: none;
        top: 0;
        left: 0;
        width: 10px;
        height: 10px;
        overflow: hidden;
        position: absolute;
    }

    .fixed-image {
        background-position: center center;
        background-size: contain;
        background-repeat: no-repeat;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }

    .fade-fixed-image-enter, .fade-fixed-image-leave-to {
        opacity: 0;
    }

    .active-player-indicator {
        position: absolute;
        bottom: 20px;
        right: 20px;
        text-transform: uppercase;
        font-weight: bold;
        color: #fff;
        opacity: 0.3;
        font-size: 20px;
    }

</style>
